import * as React from "react";
import { WebPortalClient, FvcAnalysisSet } from "webportalclient";
import LoadingWithMessage from "../misc/components/LoadingWithMessage";
import { useReferenceContextProvider } from "../Core/ReferenceContextProvider";
import { BreakdownFilter } from "./BreakdownFilter";
import Alert, { AlertCategoryEnum } from "../misc/components/Alert";
import { DonutChart } from "../Graphs/DonutChart";
import { Authorization, GlobalAuthorization } from "../misc/components/Authorization";
import { PermissionEnum } from "../misc/api/ApiPermissionEnum";
import { useCurrentStudyConfigurationContext } from "../Core/CurrentStudyConfigurationContext";
import { useSecurityContext } from "../Core/SecurityContext";
import { ErrorBoundary } from "../errors/ErrorBoundary";
import { useWebPortalApiClient } from "../misc/api/WebPortalTokenClient";

const SpirometrySessionBreakdownWidgetContent = () => {
    const client = useWebPortalApiClient();
    const [analysisSets, setAnalysisSets] = React.useState<FvcAnalysisSet[]>();
    const [from, setFrom] = React.useState<string>();
    const [to, setTo] = React.useState<string>();
    const { overreadStatuses } = useReferenceContextProvider();

    React.useEffect(() => {
        const getData = async () => {
            let filter = "type eq 'SAS_OVERREAD'";
            filter += from ? ` and created ge ${from}` : "";
            filter += to ? ` and created le ${to}` : "";

            const analysisSetResults = await client.client.getSpirometryAnalysisSets(undefined, undefined, undefined, undefined, undefined, undefined, `filter(${filter})/groupby((type,status),aggregate(id with countdistinct as total))`);
            setAnalysisSets(analysisSetResults.value);
        }

        getData();

        return () => {
            client.abortController.abort();
        }
    }, [from, to]);

    if (!analysisSets)
        return <LoadingWithMessage message="Loading Spirometry Session Breakdown..." />

    const getColor = (id: string) => {
        switch (id) {
            case "ORS_ACCEPTED":
                return "#5cb85c";
            case "ORS_PENDING":
                return "#f0ad4e"
            case "ORS_REJECTED":
                return "#d9534f"
            default:
                return "#777777"
        }
    }

    const pieData = analysisSets.map((a: any) => ({
        id: a.status,
        label: overreadStatuses.find(o => o.id === a.status).name,
        value: a.total,
        color: getColor(a.status)
    }));

    return (
        <>
            <BreakdownFilter onFilter={(from, to) => { setFrom(from); setTo(to) }} />
            <Alert show={analysisSets.length === 0} category={AlertCategoryEnum.Info}>There are no sessions within the selected timeframe</Alert>
            <div style={{ height: 400 + 'px' }}>
                <DonutChart
                    data={pieData}
                    legends={[
                        {
                            anchor: 'bottom',
                            direction: 'row',
                            justify: false,
                            translateX: 0,
                            translateY: 56,
                            itemsSpacing: 0,
                            itemWidth: 100,
                            itemHeight: 18,
                            itemTextColor: '#999',
                            itemDirection: 'left-to-right',
                            itemOpacity: 1,
                            symbolSize: 18,
                            symbolShape: 'circle',
                            effects: [
                                {
                                    on: 'hover',
                                    style: {
                                        itemTextColor: '#000'
                                    }
                                }
                            ]
                        }
                    ]}/>
            </div>
        </>
    );
}

export const SpirometrySessionBreakdownWidget = () => {
    const { currentUserPermissions } = useSecurityContext();
    const { study } = useCurrentStudyConfigurationContext();

    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionId={PermissionEnum.ViewSessionsStatusBreakdownWidget}>
            <ErrorBoundary>
                <div className="col-md-6">
                    <div id="SpirometrySessionsBreakdownWidget" className="ibox">
                        <div className="ibox-title">
                            <h5 className="text-primary">Spirometry Session Breakdown</h5>
                        </div>
                        <div className="ibox-content">
                            <SpirometrySessionBreakdownWidgetContent />
                        </div>
                    </div>
                </div>
            </ErrorBoundary>
        </Authorization>
    );
}
